import type { RefObject } from 'react';

import { cn } from '@venusprotocol/ui';
import { useTranslation } from 'libs/translations';
import { Icon, type IconProps } from '../Icon';
import { PaginationButton } from './PaginationButton';
import { usePagination } from './usePagination';

interface PaginationProps {
  itemsCount: number;
  onChange: (newPageIndex: number) => void;
  initialPageIndex?: number;
  itemsPerPageCount?: number;
  paramKey?: string;
  scrollToRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  pagesToShowCount?: number;
}

export const Pagination = ({
  itemsCount,
  onChange,
  initialPageIndex,
  itemsPerPageCount,
  paramKey,
  scrollToRef,
  className,
  pagesToShowCount,
}: PaginationProps) => {
  const {
    pagesCount,
    activePageIndex,
    goToPageByIndex,
    itemsCountString,
    pagesArray,
    minPageIndexToShow,
    maxPageIndexToShow,
  } = usePagination({
    itemsCount,
    onChange: newPageIndex => {
      onChange(newPageIndex + (initialPageIndex || 0));
    },
    itemsPerPageCount,
    paramKey,
    scrollToRef,
    pagesToShowCount,
  });

  const { t } = useTranslation();

  if (pagesCount <= 1) {
    return null;
  }

  const iconProps: IconProps = { name: 'arrowRight' };

  const isFirstPage = activePageIndex === 0;
  const isLastPage = activePageIndex === pagesCount - 1;

  return (
    <div
      className={cn(
        'my-5 flex flex-wrap items-center justify-center sm:flex-nowrap sm:justify-end',
        className,
      )}
    >
      <p className="m-0 mb-2 w-full whitespace-nowrap text-center text-base sm:mr-2 sm:mb-0 sm:w-auto sm:text-left">
        {itemsCountString}
      </p>

      {!isFirstPage && (
        <PaginationButton
          onClick={() => goToPageByIndex(0)}
          ariaLabel={t('pagination.goToFirstPage')}
        >
          <Icon className="size-6 text-inherit" name="doubleChevronLeft" />
        </PaginationButton>
      )}

      {pagesArray.map((page, index) => {
        if (index === maxPageIndexToShow) {
          return (
            <PaginationButton
              key={page}
              onClick={() => goToPageByIndex(activePageIndex + 1)}
              ariaLabel={t('pagination.goToNextPage')}
            >
              <Icon className="size-6 text-inherit" {...iconProps} />
            </PaginationButton>
          );
        }

        if (index === minPageIndexToShow) {
          return (
            <PaginationButton
              key={page}
              onClick={() => goToPageByIndex(activePageIndex - 1)}
              ariaLabel={t('pagination.goToPreviousPage')}
            >
              <Icon className="size-6 rotate-180 text-inherit" {...iconProps} />
            </PaginationButton>
          );
        }

        if (index < minPageIndexToShow || index > maxPageIndexToShow) {
          return null;
        }

        return (
          <PaginationButton
            key={page}
            onClick={() => goToPageByIndex(index)}
            className={cn(index === activePageIndex && 'text-white')}
          >
            {page}
          </PaginationButton>
        );
      })}

      {!isLastPage && (
        <PaginationButton
          onClick={() => goToPageByIndex(pagesCount - 1)}
          ariaLabel={t('pagination.goToLastPage')}
        >
          <Icon className="size-8 text-inherit" name="doubleChevronRight" />
        </PaginationButton>
      )}
    </div>
  );
};
